import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { VoiceCallDetailsData } from "@/components/VoiceCallDetailsSidebar";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { VoiceCallStatusDropdown } from "@/components/voice/VoiceCallStatusDropdown";
import {
  useVoiceCallsQuery,
  useVoiceAgentsQuery,
} from "@/hooks/voice/useVoiceCallsQuery";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Search,
  Filter,
  AlertTriangle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  PhoneCall,
  RefreshCw,
} from "lucide-react";
import { format } from "date-fns";

// Add skeleton imports
import {
  Skeleton,
  SkeletonText,
  SkeletonTable,
  SkeletonForm,
} from "@/components/ui/skeleton";

interface VoiceCallRow {
  id: string;
  customer_name?: string;
  customer_number?: string;
  phone_number?: string;
  duration_seconds?: number;
  total_cost?: number;
  call_status?: string; // VAPI technical status (scheduled, queued, ringing, in-progress, forwarding, ended, failed)
  status?: string; // Business workflow status (open, action_required, in_progress, closed, archived)
  call_type?: string;
  end_reason?: string;
  summary?: string;
  transcript?: string;
  recording_url?: string;
  started_at?: string;
  ended_at?: string;
  created_at: string;
  transfer_destination?: string;
  vapi_call_id: string;
  voice_agent_id: string;
  extracted_sentiment?: string;
  extracted_priority?: string;
  intent?: string;
  next_step?: string;
  tags?: string[];
  voice_agents?: {
    id: string;
    title: string;
    agent_name: string;
    organization_id: string;
  };
}

type SortField = "date" | "intent" | "sentiment" | "priority" | "status";
type SortDirection = "asc" | "desc" | null;

interface VoiceCallsListProps {
  onCallClick?: (callData: VoiceCallDetailsData) => void;
  setLoadingCallDetails?: (loading: boolean) => void;
}

// Helper function to format relative dates with Eastern time
const formatRelativeDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  let dateLabel = "";
  if (dateOnly.getTime() === today.getTime()) {
    dateLabel = "Today";
  } else if (dateOnly.getTime() === yesterday.getTime()) {
    dateLabel = "Yesterday";
  } else if (date.getFullYear() === now.getFullYear()) {
    dateLabel = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } else {
    dateLabel = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  // Add Eastern time if available
  const easternTime = date.toLocaleTimeString("en-US", {
    timeZone: "America/New_York",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return { dateLabel, easternTime };
};

// Helper function to format duration in seconds to readable format
const formatDuration = (durationSeconds: number | null | undefined): string => {
  if (!durationSeconds || durationSeconds === 0) {
    return "-";
  }

  const minutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;

  if (minutes === 0) {
    return `${seconds}s`;
  } else {
    return `${minutes}m ${seconds}s`;
  }
};

// Helper function to get call type badge variant
const getCallTypeBadgeProps = (callType: string | null | undefined) => {
  switch (callType) {
    case "inbound":
      return {
        className:
          "capitalize text-xs bg-blue-100 text-blue-800 border-blue-200",
        variant: "outline" as const,
      };
    case "outbound":
      return {
        className:
          "capitalize text-xs bg-green-100 text-green-800 border-green-200",
        variant: "outline" as const,
      };
    default:
      return {
        className: "capitalize text-xs border-gray-300 text-gray-700",
        variant: "outline" as const,
      };
  }
};

// Helper function to get priority badge variant
const getPriorityVariant = (priority: string | null | undefined) => {
  switch (priority) {
    case "urgent":
      return {
        className: "capitalize text-xs bg-red-100 text-red-800 border-red-200",
        variant: "outline" as const,
      };
    case "high":
      return {
        className:
          "capitalize text-xs bg-orange-100 text-orange-800 border-orange-200",
        variant: "outline" as const,
      };
    case "medium":
      return {
        className:
          "capitalize text-xs bg-yellow-100 text-yellow-800 border-yellow-200",
        variant: "outline" as const,
      };
    case "low":
      return {
        className:
          "capitalize text-xs bg-green-100 text-green-800 border-green-200",
        variant: "outline" as const,
      };
    default:
      return {
        className: "capitalize text-xs border-gray-300 text-gray-600",
        variant: "outline" as const,
      };
  }
};

// Helper function to get sentiment badge variant
const getSentimentVariant = (sentiment: string | null | undefined) => {
  switch (sentiment) {
    case "positive":
      return {
        className:
          "capitalize text-xs bg-green-100 text-green-800 border-green-200",
        variant: "outline" as const,
      };
    case "neutral":
      return {
        className:
          "capitalize text-xs bg-blue-100 text-blue-800 border-blue-200",
        variant: "outline" as const,
      };
    case "negative":
      return {
        className: "capitalize text-xs bg-red-100 text-red-800 border-red-200",
        variant: "outline" as const,
      };
    default:
      return {
        className: "capitalize text-xs border-gray-300 text-gray-600",
        variant: "outline" as const,
      };
  }
};

// Helper function to get call status badge props (VAPI technical status)
const getCallStatusBadgeProps = (callStatus: string | null | undefined) => {
  switch (callStatus) {
    case "ended":
      return {
        className:
          "capitalize text-xs bg-green-100 text-green-800 border-green-200",
        variant: "outline" as const,
      };
    case "failed":
      return {
        className: "capitalize text-xs bg-red-100 text-red-800 border-red-200",
        variant: "outline" as const,
      };
    case "in-progress":
      return {
        className:
          "capitalize text-xs bg-blue-100 text-blue-800 border-blue-200",
        variant: "outline" as const,
      };
    case "ringing":
      return {
        className:
          "capitalize text-xs bg-yellow-100 text-yellow-800 border-yellow-200",
        variant: "outline" as const,
      };
    case "queued":
      return {
        className:
          "capitalize text-xs bg-purple-100 text-purple-800 border-purple-200",
        variant: "outline" as const,
      };
    case "scheduled":
      return {
        className:
          "capitalize text-xs bg-indigo-100 text-indigo-800 border-indigo-200",
        variant: "outline" as const,
      };
    case "forwarding":
      return {
        className:
          "capitalize text-xs bg-orange-100 text-orange-800 border-orange-200",
        variant: "outline" as const,
      };
    default:
      return {
        className: "capitalize text-xs border-gray-300 text-gray-600",
        variant: "outline" as const,
      };
  }
};

// Helper function to get status badge props (business workflow status)
const getStatusBadgeProps = (status: string | null | undefined) => {
  switch (status) {
    case "open":
      return {
        className:
          "capitalize text-xs bg-blue-100 text-blue-800 border-blue-200",
        variant: "outline" as const,
      };
    case "action_required":
      return {
        className: "capitalize text-xs bg-red-100 text-red-800 border-red-200",
        variant: "outline" as const,
      };
    case "in_progress":
      return {
        className:
          "capitalize text-xs bg-yellow-100 text-yellow-800 border-yellow-200",
        variant: "outline" as const,
      };
    case "closed":
      return {
        className:
          "capitalize text-xs bg-green-100 text-green-800 border-green-200",
        variant: "outline" as const,
      };
    case "archived":
      return {
        className:
          "capitalize text-xs bg-gray-100 text-gray-800 border-gray-200",
        variant: "outline" as const,
      };
    default:
      return {
        className: "capitalize text-xs border-gray-300 text-gray-600",
        variant: "outline" as const,
      };
  }
};

export default function VoiceCallsList({
  onCallClick,
  setLoadingCallDetails,
}: VoiceCallsListProps) {
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  // Use TanStack Query hooks for efficient data fetching
  const {
    data: calls = [],
    isLoading: isLoadingCalls,
    error: callsError,
    refetch: refreshCalls,
  } = useVoiceCallsQuery();

  const {
    data: voiceAgents = [],
    isLoading: isLoadingAgents,
    error: agentsError,
    refetch: refreshAgents,
  } = useVoiceAgentsQuery();

  // Local UI state only
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAgentId, setSelectedAgentId] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [callStatusFilter, setCallStatusFilter] = useState<string>("all");
  const [sentimentFilter, setSentimentFilter] = useState<string>("all");
  const [showHighPriority, setShowHighPriority] = useState(false);
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Parse URL query parameters for agent filtering
  const searchParams = new URLSearchParams(location.search);
  const agentParam = searchParams.get("agent");

  // Set initial filter from URL parameter
  useEffect(() => {
    if (agentParam && agentParam !== selectedAgentId) {
      setSelectedAgentId(agentParam);
    }
  }, [agentParam]);

  // Update URL when agent filter changes
  useEffect(() => {
    const currentAgentParam = searchParams.get("agent");

    // Don't update URL if we're already on /voice-calls without query params
    // and the selected agent is "all"
    if (selectedAgentId === "all" && !currentAgentParam) {
      return;
    }

    if (selectedAgentId === "all") {
      navigate("/voice-calls", { replace: true });
    } else if (
      selectedAgentId !== "all" &&
      currentAgentParam !== selectedAgentId
    ) {
      navigate(`/voice-calls?agent=${selectedAgentId}`, { replace: true });
    }
  }, [selectedAgentId, navigate, location.search]);

  // Show errors from context
  useEffect(() => {
    if (callsError) {
      toast({
        variant: "destructive",
        title: "Failed to load calls",
        description: callsError.message,
      });
    }
  }, [callsError, toast]);

  useEffect(() => {
    if (agentsError) {
      toast({
        variant: "destructive",
        title: "Failed to load agents",
        description: agentsError.message,
      });
    }
  }, [agentsError, toast]);

  // Real-time updates: Subscribe to voice_calls changes
  const { organization } = useAuth();
  useEffect(() => {
    if (!organization?.id) return;

    const channel = supabase
      .channel("voice_calls_changes")
      .on(
        "postgres_changes",
        {
          event: "*", // Listen to all events (INSERT, UPDATE, DELETE)
          schema: "public",
          table: "voice_calls",
        },
        () => {
          // Refetch calls when any change occurs
          refreshCalls();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [organization?.id, refreshCalls]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(
        sortDirection === "asc"
          ? "desc"
          : sortDirection === "desc"
            ? null
            : "asc",
      );
      if (sortDirection === "desc") {
        setSortField("date");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3" />;
    }

    if (sortDirection === "asc") {
      return <ArrowUp className="h-3 w-3" />;
    } else if (sortDirection === "desc") {
      return <ArrowDown className="h-3 w-3" />;
    }

    return <ArrowUpDown className="h-3 w-3" />;
  };

  const handleCallClick = async (call: VoiceCallRow) => {
    if (!onCallClick) return;

    console.log("Call clicked:", call.customer_name, call.id);

    if (setLoadingCallDetails) {
      setLoadingCallDetails(true);
    }

    try {
      // Fetch form fields for this voice call
      const { data: fieldValues } = await supabase
        .from("conversation_field_values")
        .select("id, value, field_id, form_fields(title, field_type)")
        .eq("voice_call_id", call.id);

      const formFields =
        fieldValues?.map((fv: any) => ({
          field_id: fv.field_id || "",
          title: fv.form_fields?.title || "Unknown Field",
          value: fv.value || "",
          field_type: fv.form_fields?.field_type || "post_conversation",
        })) || [];

      const callDetailsData: VoiceCallDetailsData = {
        id: call.id,
        customer_name: call.customer_name,
        customer_number: call.customer_number,
        phone_number: call.phone_number,
        duration_seconds: call.duration_seconds,
        total_cost: call.total_cost,
        status: call.status,
        call_type: call.call_type,
        end_reason: call.end_reason,
        extracted_priority: call.extracted_priority as
          | "low"
          | "medium"
          | "high"
          | "urgent"
          | null,
        extracted_sentiment: call.extracted_sentiment as
          | "positive"
          | "neutral"
          | "negative"
          | "unknown"
          | null,
        intent: call.intent,
        next_step: call.next_step,
        tags: call.tags,
        summary: call.summary,
        transcript: call.transcript,
        recording_url: call.recording_url,
        started_at: call.started_at,
        ended_at: call.ended_at,
        created_at: call.created_at,
        transfer_destination: call.transfer_destination,
        voice_agent: call.voice_agents
          ? {
              name: call.voice_agents.title,
              agent_name: call.voice_agents.agent_name,
              title: call.voice_agents.title,
            }
          : null,
        form_fields: formFields.length > 0 ? formFields : undefined,
      };

      onCallClick(callDetailsData);
    } catch (error) {
      console.error("Error fetching call details:", error);

      const callDetailsData: VoiceCallDetailsData = {
        id: call.id,
        customer_name: call.customer_name,
        customer_number: call.customer_number,
        phone_number: call.phone_number,
        duration_seconds: call.duration_seconds,
        total_cost: call.total_cost,
        status: call.status,
        call_type: call.call_type,
        end_reason: call.end_reason,
        extracted_priority: call.extracted_priority as
          | "low"
          | "medium"
          | "high"
          | "urgent"
          | null,
        extracted_sentiment: call.extracted_sentiment as
          | "positive"
          | "neutral"
          | "negative"
          | "unknown"
          | null,
        intent: call.intent,
        next_step: call.next_step,
        tags: call.tags,
        summary: call.summary,
        transcript: call.transcript,
        recording_url: call.recording_url,
        started_at: call.started_at,
        ended_at: call.ended_at,
        created_at: call.created_at,
        transfer_destination: call.transfer_destination,
        voice_agent: call.voice_agents
          ? {
              name: call.voice_agents.title,
              agent_name: call.voice_agents.agent_name,
              title: call.voice_agents.title,
            }
          : null,
      };
      onCallClick(callDetailsData);
    } finally {
      if (setLoadingCallDetails) {
        setLoadingCallDetails(false);
      }
    }
  };

  const filteredAndSortedCalls = useMemo(() => {
    const filtered = calls.filter((call) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          call.customer_number?.toLowerCase().includes(searchLower) ||
          call.customer_name?.toLowerCase().includes(searchLower) ||
          call.voice_agents?.agent_name?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Agent filter
      if (selectedAgentId !== "all") {
        if (call.voice_agent_id !== selectedAgentId) return false;
      }

      // Business status filter
      if (statusFilter !== "all" && call.status !== statusFilter) return false;

      // VAPI call status filter
      if (callStatusFilter !== "all" && call.call_status !== callStatusFilter)
        return false;

      // High priority filter
      if (showHighPriority) {
        if (
          call.extracted_priority !== "high" &&
          call.extracted_priority !== "urgent"
        )
          return false;
      }

      // Sentiment filter
      if (sentimentFilter !== "all") {
        if (call.extracted_sentiment !== sentimentFilter) return false;
      }

      return true;
    });

    if (sortField && sortDirection) {
      filtered.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortField) {
          case "date":
            aValue = new Date(a.created_at);
            bValue = new Date(b.created_at);
            break;
          case "priority":
            aValue = a.extracted_priority || "";
            bValue = b.extracted_priority || "";
            break;
          case "sentiment":
            aValue = a.extracted_sentiment || "";
            bValue = b.extracted_sentiment || "";
            break;
          case "intent":
            aValue = a.intent || "";
            bValue = b.intent || "";
            break;
          case "status":
            aValue = a.status || "";
            bValue = b.status || "";
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortDirection === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDirection === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [
    calls,
    searchTerm,
    selectedAgentId,
    statusFilter,
    callStatusFilter,
    showHighPriority,
    sentimentFilter,
    sortField,
    sortDirection,
  ]);

  // Show full skeleton only on initial load (when no calls exist yet)
  const isInitialLoading = isLoadingCalls && calls.length === 0;

  if (isInitialLoading) {
    return (
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Filters Section Skeleton - Match actual filters layout */}
        <div className="flex-shrink-0 p-6 pb-0 bg-white">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Left side filters */}
            <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4">
              {/* Search skeleton */}
              <div className="relative w-full sm:w-64">
                <Skeleton className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" />
                <Skeleton className="w-full h-10" />
              </div>

              {/* Agent Filter skeleton */}
              <div className="flex items-center gap-2">
                <Skeleton className="w-4 h-4" />
                <Skeleton className="w-[160px] h-10" />
              </div>

              {/* Business Status Filter skeleton */}
              <div className="flex items-center gap-2">
                <Skeleton className="w-4 h-4" />
                <Skeleton className="w-[130px] h-10" />
              </div>

              {/* Call Status Filter skeleton */}
              <div className="flex items-center gap-2">
                <Skeleton className="w-4 h-4" />
                <Skeleton className="w-[120px] h-10" />
              </div>

              {/* High Priority Filter skeleton */}
              <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-md bg-gray-50">
                <Skeleton className="w-4 h-4" />
                <Skeleton className="w-16 h-4" />
              </div>

              {/* Sentiment Filter skeleton */}
              <div className="flex items-center gap-2">
                <Skeleton className="w-4 h-4" />
                <Skeleton className="w-[140px] h-10" />
              </div>
            </div>

            {/* Results count skeleton */}
            <div className="flex items-center justify-end lg:justify-start">
              <Skeleton className="w-16 h-4" />
            </div>
          </div>
        </div>

        {/* Scrollable Table Container Skeleton */}
        <div className="flex-1 p-6 pt-6 overflow-hidden">
          <SkeletonTable rows={8} columns={9} showHeader={true} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Filters Section - Fixed at top */}
      <div className="flex-shrink-0 p-6 pb-0 bg-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Filters and Search - Left side on desktop, stacked on mobile */}
          <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4">
            {/* Search */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="By name, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full bg-white border-gray-300"
              />
            </div>

            {/* Agent Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <Select
                value={selectedAgentId}
                onValueChange={setSelectedAgentId}
              >
                <SelectTrigger className="w-[160px] bg-white border-gray-300">
                  <SelectValue placeholder="Filter by agent..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  {voiceAgents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.agent_name || agent.title || "Unnamed Agent"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Business Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[130px] bg-white border-gray-300">
                  <SelectValue placeholder="Business Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="action_required">
                    Action Required
                  </SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Call Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <Select
                value={callStatusFilter}
                onValueChange={setCallStatusFilter}
              >
                <SelectTrigger className="w-[120px] bg-white border-gray-300">
                  <SelectValue placeholder="Call Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Calls</SelectItem>
                  <SelectItem value="ended">Ended</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="ringing">Ringing</SelectItem>
                  <SelectItem value="queued">Queued</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="forwarding">Forwarding</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* High Priority Filter */}
            <Button
              variant={showHighPriority ? "default" : "outline"}
              size="sm"
              onClick={() => setShowHighPriority(!showHighPriority)}
              className="flex items-center gap-2 border-gray-300 whitespace-nowrap"
            >
              <AlertTriangle className="h-4 w-4" />
              <span className="hidden sm:inline">High Priority</span>
              <span className="sm:hidden">Priority</span>
            </Button>

            {/* Sentiment Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <Select
                value={sentimentFilter}
                onValueChange={setSentimentFilter}
              >
                <SelectTrigger className="w-[140px] bg-white border-gray-300">
                  <SelectValue placeholder="Sentiment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sentiment</SelectItem>
                  <SelectItem value="positive">Positive</SelectItem>
                  <SelectItem value="neutral">Neutral</SelectItem>
                  <SelectItem value="negative">Negative</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results Count - Right side on desktop, below on mobile */}
          <div className="flex items-center justify-end lg:justify-start">
            <p className="text-sm text-gray-600">
              {filteredAndSortedCalls.length} calls
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refreshCalls()}
              disabled={isLoadingCalls && calls.length > 0}
              className="ml-2 hover:bg-gray-100"
              title="Refresh calls (auto-refresh: 30s)"
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoadingCalls && calls.length > 0 ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </div>
      </div>

      {/* Scrollable Table Container */}
      <div className="flex-1 p-6 pt-6 overflow-hidden">
        <div className="bg-white rounded-lg h-full flex flex-col relative">
          {/* Loading overlay for refresh (only when calls exist) */}
          {isLoadingCalls && calls.length > 0 && (
            <div className="absolute inset-0 bg-white/50 backdrop-blur-sm z-20 flex items-start justify-center pt-8">
              <div className="bg-white px-4 py-2 rounded-lg shadow-sm border flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
                <span className="text-sm text-gray-600">
                  Refreshing calls...
                </span>
              </div>
            </div>
          )}

          {/* Scrollable Table with Fixed Header */}
          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-gray-50 z-10">
                <TableRow>
                  <TableHead className="min-w-[200px] text-xs font-medium text-[#667085] py-4">
                    Customer
                  </TableHead>
                  <TableHead className="min-w-[120px] text-xs font-medium text-[#667085]">
                    <Button
                      variant="ghost"
                      onClick={() => handleSort("date")}
                      className="h-auto p-0 text-xs font-medium text-[#667085] hover:bg-transparent hover:text-gray-900"
                    >
                      Date
                      <span className="ml-1 text-[#667085]">
                        {getSortIcon("date")}
                      </span>
                    </Button>
                  </TableHead>
                  <TableHead className="min-w-[80px] text-xs font-medium text-[#667085]">
                    Duration
                  </TableHead>
                  <TableHead className="min-w-[100px] text-xs font-medium text-[#667085]">
                    <Button
                      variant="ghost"
                      onClick={() => handleSort("priority")}
                      className="h-auto p-0 text-xs font-medium text-[#667085] hover:bg-transparent hover:text-gray-900"
                    >
                      Priority
                      <span className="ml-1 text-[#667085]">
                        {getSortIcon("priority")}
                      </span>
                    </Button>
                  </TableHead>
                  <TableHead className="min-w-[200px] text-xs font-medium text-[#667085] hidden lg:table-cell">
                    <Button
                      variant="ghost"
                      onClick={() => handleSort("intent")}
                      className="h-auto p-0 text-xs font-medium text-[#667085] hover:bg-transparent hover:text-gray-900"
                    >
                      Intent
                      <span className="ml-1 text-[#667085]">
                        {getSortIcon("intent")}
                      </span>
                    </Button>
                  </TableHead>
                  <TableHead className="min-w-[176px] text-xs font-medium text-[#667085] hidden lg:table-cell">
                    <Button
                      variant="ghost"
                      onClick={() => handleSort("status")}
                      className="h-auto p-0 text-xs font-medium text-[#667085] hover:bg-transparent hover:text-gray-900"
                    >
                      Status
                      <span className="ml-1 text-[#667085]">
                        {getSortIcon("status")}
                      </span>
                    </Button>
                  </TableHead>
                  <TableHead className="min-w-[200px] text-xs font-medium text-[#667085] hidden lg:table-cell">
                    Next Steps
                  </TableHead>
                  <TableHead className="min-w-[100px] text-xs font-medium text-[#667085] hidden md:table-cell">
                    <Button
                      variant="ghost"
                      onClick={() => handleSort("sentiment")}
                      className="h-auto p-0 text-xs font-medium text-[#667085] hover:bg-transparent hover:text-gray-900"
                    >
                      Sentiment
                      <span className="ml-1 text-[#667085]">
                        {getSortIcon("sentiment")}
                      </span>
                    </Button>
                  </TableHead>
                  <TableHead className="min-w-[400px] text-xs font-medium text-[#667085] hidden lg:table-cell">
                    Tags
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedCalls.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12">
                      <PhoneCall className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">
                        No calls found
                      </h3>
                      <p className="text-muted-foreground">
                        Try adjusting your search or filter criteria
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedCalls.map((call, index) => {
                    const callTypeProps = getCallTypeBadgeProps(call.call_type);
                    const priorityProps = getPriorityVariant(
                      call.extracted_priority,
                    );
                    const sentimentProps = getSentimentVariant(
                      call.extracted_sentiment,
                    );
                    const callStatusProps = getCallStatusBadgeProps(
                      call.call_status,
                    );

                    return (
                      <TableRow
                        key={call.id}
                        className={`cursor-pointer hover:bg-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-gray-50/30"}`}
                        onClick={() => handleCallClick(call)}
                      >
                        {/* Customer Column */}
                        <TableCell className="py-4">
                          <div className="flex flex-col">
                            <div
                              className="font-medium text-gray-900"
                              style={{ fontWeight: 500 }}
                            >
                              {call.customer_name || "Unknown Customer"}
                            </div>
                            <div className="text-sm text-gray-500">
                              <div className="truncate">
                                {call.customer_number || "No phone number"}
                              </div>
                            </div>
                          </div>
                        </TableCell>

                        {/* Date Column */}
                        <TableCell className="py-4">
                          <div className="text-sm text-gray-600">
                            {formatRelativeDate(call.created_at).dateLabel}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatRelativeDate(call.created_at).easternTime}
                          </div>
                        </TableCell>

                        {/* Duration Column */}
                        <TableCell className="py-4">
                          <div className="text-sm text-gray-700">
                            {formatDuration(call.duration_seconds)}
                          </div>
                        </TableCell>

                        {/* Priority Column */}
                        <TableCell className="py-4">
                          {call.extracted_priority ? (
                            <Badge
                              variant={priorityProps.variant}
                              className={priorityProps.className}
                            >
                              {call.extracted_priority}
                            </Badge>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </TableCell>

                        {/* Intent Column - Hidden on mobile */}
                        <TableCell className="py-4 hidden lg:table-cell">
                          <div
                            className="text-sm text-gray-700 max-w-[200px] truncate"
                            title={call.intent || ""}
                          >
                            {call.intent || (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                        </TableCell>

                        {/* Status Column - Hidden on mobile */}
                        <TableCell className="py-4 hidden lg:table-cell">
                          <VoiceCallStatusDropdown
                            callId={call.id}
                            currentStatus={call.status as any}
                            onStatusChange={(newStatus) => {
                              // Status change will trigger a refresh of the data
                              // The context will handle the update
                            }}
                            size="sm"
                          />
                        </TableCell>

                        {/* Next Steps Column - Hidden on mobile */}
                        <TableCell className="py-4 hidden lg:table-cell">
                          <div
                            className="text-sm text-gray-700 max-w-[200px] truncate"
                            title={call.next_step || ""}
                          >
                            {call.next_step || (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                        </TableCell>

                        {/* Sentiment Column - Hidden on small screens */}
                        <TableCell className="py-4 hidden md:table-cell">
                          {call.extracted_sentiment &&
                          call.extracted_sentiment !== "unknown" ? (
                            <Badge
                              variant={sentimentProps.variant}
                              className={sentimentProps.className}
                            >
                              {call.extracted_sentiment}
                            </Badge>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </TableCell>

                        {/* Tags Column - Hidden on mobile */}
                        <TableCell className="py-4 hidden lg:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {call.tags && call.tags.length > 0 ? (
                              call.tags.slice(0, 2).map((tag, index) => (
                                <Badge
                                  key={index}
                                  variant="secondary"
                                  className="text-xs bg-gray-100 text-gray-700 border-0"
                                >
                                  {tag}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                            {call.tags && call.tags.length > 2 && (
                              <Badge
                                variant="outline"
                                className="text-xs border-gray-300 text-gray-600"
                              >
                                +{call.tags.length - 2}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
